
import React, { useEffect, useState } from 'react';
import { Download, Share, PlusSquare, X } from 'lucide-react';

export const InstallPWA: React.FC = () => {
  const [supportsPWA, setSupportsPWA] = useState(false);
  const [promptInstall, setPromptInstall] = useState<any>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
    if (isStandalone) return;

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    setIsIOS(ios);

    if (ios) {
        // Show instruction for iOS after a small delay
        setTimeout(() => setIsVisible(true), 2000);
    } else if (isAndroid) {
        // If beforeinstallprompt doesn't fire within 3s, show manual Android instructions
        (window as any).__androidTimer = setTimeout(() => {
            setIsVisible(true);
        }, 3000);
    }

    // Capture event for Android/Chrome
    const handler = (e: any) => {
      e.preventDefault();
      if ((window as any).__androidTimer) clearTimeout((window as any).__androidTimer);
      setPromptInstall(e);
      setSupportsPWA(true);
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!promptInstall) {
      return;
    }
    promptInstall.prompt();
    promptInstall.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
            setIsVisible(false);
        }
    });
  };

  const handleDismiss = () => {
      setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-20 md:bottom-6 left-4 right-4 z-[100] animate-fade-in-up">
      <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-4 relative overflow-hidden">
        {/* Close Button */}
        <button 
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-slate-500 hover:text-white p-1"
        >
            <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-4 pr-6">
            <div className="bg-orange-600 p-3 rounded-xl shadow-lg shadow-orange-900/20 shrink-0">
                <Download className="w-6 h-6 text-white" />
            </div>
            <div>
                <h4 className="font-bold text-white text-base">Instalar App</h4>
                <p className="text-sm text-slate-400 mt-1 leading-relaxed">
                    {isIOS 
                        ? "Adicione à tela inicial para melhor experiência." 
                        : "Instale o Driver Score para acesso rápido e melhor performance."}
                </p>

                {/* Android / Chrome Button */}
                {supportsPWA && !isIOS && (
                    <button
                        onClick={handleInstallClick}
                        className="mt-3 bg-white text-slate-900 font-bold px-4 py-2 rounded-lg text-sm hover:bg-slate-200 transition-colors flex items-center gap-2"
                    >
                        <Download className="w-4 h-4" />
                        Instalar Agora
                    </button>
                )}

                {/* Manual Android Instructions (When button is not supported but it's an Android browser) */}
                {!supportsPWA && !isIOS && (
                    <div className="mt-3 text-xs sm:text-sm text-slate-300 bg-slate-900/50 p-3 rounded-lg border border-slate-700">
                        <p className="leading-relaxed">
                            Para instalar: Toque nos <span className="font-bold text-white">três pontinhos (⋮)</span> do navegador e selecione <span className="font-bold text-white">Adicionar à Tela Inicial</span>.
                        </p>
                    </div>
                )}

                {/* iOS Instructions */}
                {isIOS && (
                    <div className="mt-3 text-xs sm:text-sm text-slate-300 bg-slate-900/50 p-3 rounded-lg border border-slate-700">
                        <p className="leading-relaxed">
                            Para instalar: Toque no ícone de compartilhar 
                            <span className="inline-flex align-middle mx-1"><Share className="w-4 h-4 text-blue-400" /></span> 
                            e selecione <span className="font-bold text-white">Adicionar à Tela de Início</span>.
                        </p>
                    </div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};
